import React, { useEffect, useMemo, useState, useCallback } from "react";
import AppShell from "@/components/ascend/AppShell";
import api, { formatApiError } from "@/lib/api";
import {
  Newspaper, ExternalLink, RefreshCw, Users, Globe, Bookmark, BookmarkCheck,
  MapPin, Twitter, GraduationCap, Link2, MessageCircle, X as XIcon,
} from "lucide-react";
import { toast, Toaster } from "sonner";

const REGION_LENSES = [
  { id: "global",  label: "🌍 Global",  aria: "Global lens" },
  { id: "western", label: "Western",    aria: "Western sources" },
  { id: "asian",   label: "Asian",      aria: "Asian sources" },
];

const ARTICLE_CATEGORIES = [
  { id: "all",           label: "All" },
  { id: "neuroscience",  label: "Neuroscience" },
  { id: "psychology",    label: "Psychology" },
  { id: "mental_health", label: "Mental Health" },
];

const PEOPLE_TOPICS = [
  { id: "all",          label: "All" },
  { id: "neuroscience", label: "Neuroscience" },
  { id: "psychology",   label: "Psychology" },
  { id: "sleep",        label: "Sleep" },
  { id: "habits",       label: "Habits" },
  { id: "focus",        label: "Focus" },
];

const INITIAL_ARTICLE_COUNT = 30;
const LOAD_MORE_STEP = 30;

/** ISO 3166 alpha-2 → 🇦🇦 Unicode flag emoji. */
function flagEmoji(cc) {
  if (!cc || cc.length !== 2) return "";
  const A = 0x1f1e6;
  const c0 = cc.toUpperCase().charCodeAt(0) - 65;
  const c1 = cc.toUpperCase().charCodeAt(1) - 65;
  if (c0 < 0 || c1 < 0 || c0 > 25 || c1 > 25) return "";
  return String.fromCodePoint(A + c0) + String.fromCodePoint(A + c1);
}

/** Client-side sha1 of the article link so we can key optimistic bookmarks. */
async function sha1Hex(str) {
  if (typeof window !== "undefined" && window.crypto?.subtle) {
    const buf = new TextEncoder().encode(str);
    const hash = await window.crypto.subtle.digest("SHA-1", buf);
    return Array.from(new Uint8Array(hash)).map((b) => b.toString(16).padStart(2, "0")).join("");
  }
  return str;
}

function formatRelative(iso) {
  if (!iso) return "recently";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "recently";
  const diff = (Date.now() - then) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  const days = Math.floor(diff / 86400);
  if (days === 1) return "yesterday";
  if (days < 7) return `${days} days ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function initials(name = "", nameNative = null) {
  if (nameNative) {
    // First CJK-range character of the native name is a lovely touch for
    // Asian experts. Falls back to Latin initial for names without native.
    for (const ch of nameNative.trim()) {
      // Any non-ASCII first codepoint is fair game — Chinese, Japanese, etc.
      if (ch.trim() && ch.charCodeAt(0) > 127) return ch;
    }
  }
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase() || "?";
}

export default function News() {
  const [regionLens, setRegionLens] = useState("global");
  const [articleCategory, setArticleCategory] = useState("all");
  const [originFilter, setOriginFilter] = useState(null); // {code, label} | null
  const [articles, setArticles] = useState([]);
  const [articleLimit, setArticleLimit] = useState(INITIAL_ARTICLE_COUNT);
  const [articleTotal, setArticleTotal] = useState(0);
  const [articleLoading, setArticleLoading] = useState(true);

  const [peopleTopic, setPeopleTopic] = useState("all");
  const [people, setPeople] = useState([]);
  const [peopleLoading, setPeopleLoading] = useState(true);

  // Saved articles ("Read Weekend"). We keep a hash-set for O(1) toggle state.
  const [savedHashes, setSavedHashes] = useState(new Set());

  const loadArticles = async (cat, limit, region, originCode) => {
    setArticleLoading(true);
    try {
      const params = { category: cat, limit, region };
      if (originCode) params.origin_country_code = originCode;
      const { data } = await api.get(`/news/articles`, { params });
      setArticles(data.items || []);
      setArticleTotal(data.total_available ?? 0);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setArticleLoading(false);
    }
  };

  const loadPeople = async (topic, region) => {
    setPeopleLoading(true);
    try {
      const { data } = await api.get(`/news/people`, { params: { topic, region } });
      setPeople(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPeopleLoading(false);
    }
  };

  const loadSaved = useCallback(async () => {
    try {
      const { data } = await api.get(`/news/saved`);
      setSavedHashes(new Set((data.items || []).map((a) => a.article_hash)));
    } catch (_) {
      // silent — News still works without saved-state
    }
  }, []);

  useEffect(() => { loadArticles(articleCategory, articleLimit, regionLens, originFilter?.code); /* eslint-disable-next-line */ }, [articleCategory, articleLimit, regionLens, originFilter]);
  useEffect(() => { loadPeople(peopleTopic, regionLens); /* eslint-disable-next-line */ }, [peopleTopic, regionLens]);
  useEffect(() => { loadSaved(); /* mark that news has been visited so the dashboard tile can show up */ try { localStorage.setItem("ascend_news_visited", "1"); } catch (_) {} }, [loadSaved]);

  const switchCategory = (id) => {
    if (id === articleCategory) return;
    setArticleLimit(INITIAL_ARTICLE_COUNT);
    setArticleCategory(id);
  };

  const switchLens = (id) => {
    if (id === regionLens) return;
    setArticleLimit(INITIAL_ARTICLE_COUNT);
    setOriginFilter(null); // switching lens clears the country focus
    setRegionLens(id);
  };

  const setCountryFocus = (code, label) => {
    if (!code) return;
    setArticleLimit(INITIAL_ARTICLE_COUNT);
    setOriginFilter({ code, label: label || code });
  };

  const clearCountryFocus = () => {
    setArticleLimit(INITIAL_ARTICLE_COUNT);
    setOriginFilter(null);
  };

  const toggleSave = async (article) => {
    const hash = await sha1Hex(article.link);
    const wasSaved = savedHashes.has(hash);
    // Optimistic toggle
    setSavedHashes((s) => {
      const next = new Set(s);
      if (wasSaved) next.delete(hash); else next.add(hash);
      return next;
    });
    try {
      if (wasSaved) {
        await api.delete(`/news/saved/${hash}`);
        toast.success("Removed from Read Weekend");
      } else {
        await api.post(`/news/saved`, {
          title: article.title,
          link: article.link,
          summary: article.summary,
          source_name: article.source_name,
          source_country_code: article.origin_country_code,
          image_url: article.image_url,
          published_at: article.published_at,
        });
        toast.success("Saved to Read Weekend");
      }
    } catch (e) {
      // rollback
      setSavedHashes((s) => {
        const next = new Set(s);
        if (wasSaved) next.add(hash); else next.delete(hash);
        return next;
      });
      toast.error(formatApiError(e));
    }
  };

  // Precompute hash-per-article so the card knows its saved state without an
  // async lookup on every render.
  const [hashByLink, setHashByLink] = useState({});
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const pairs = await Promise.all(
        articles.map(async (a) => [a.link, await sha1Hex(a.link)])
      );
      if (cancelled) return;
      setHashByLink(Object.fromEntries(pairs));
    })();
    return () => { cancelled = true; };
  }, [articles]);

  const hasMore = articles.length < articleTotal && articles.length < 200;

  return (
    <AppShell>
      <Toaster richColors position="top-center" />
      <div className="fade-up space-y-12" data-testid="news-page">
        {/* Global Lens — regional filter */}
        <section aria-label="Global lens filter">
          <div className="flex flex-col sm:flex-row sm:items-center sm:gap-4 gap-2">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted">
              <Globe size={14} strokeWidth={1.5} style={{ color: "var(--primary)" }} />
              Global lens
            </div>
            <div className="flex flex-wrap items-center gap-2" role="tablist" data-testid="news-region-chips">
              {REGION_LENSES.map((r) => {
                const active = regionLens === r.id;
                return (
                  <button
                    key={r.id}
                    onClick={() => switchLens(r.id)}
                    aria-label={r.aria}
                    aria-pressed={active}
                    className="px-4 py-1.5 rounded-full text-sm transition-all"
                    style={{
                      background: active ? "var(--surface-2)" : "transparent",
                      color: active ? "var(--primary)" : "var(--text-muted)",
                      border: `1px solid ${active ? "var(--primary)" : "var(--divider)"}`,
                    }}
                    data-testid={`news-region-${r.id}`}
                  >
                    {r.label}
                  </button>
                );
              })}
            </div>
          </div>
          <p className="font-hand text-base mt-2" style={{ color: "var(--accent)" }}>
            see the field from every angle — she'll thank you later
          </p>
        </section>

        {/* Articles */}
        <section>
          <header className="mb-5">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted">
              <Newspaper size={14} strokeWidth={1.5} style={{ color: "var(--primary)" }} />
              Field notes
            </div>
            <h1 className="font-display text-4xl sm:text-5xl mt-1" style={{ color: "var(--text)" }}>
              The field, right now
            </h1>
            <p className="font-hand text-lg mt-2" style={{ color: "var(--accent)" }}>
              fresh from the labs and journals she'll live in
            </p>
          </header>

          <div className="flex flex-wrap items-center gap-2 mb-6" role="tablist" data-testid="news-category-chips">
            {ARTICLE_CATEGORIES.map((c) => {
              const active = articleCategory === c.id;
              return (
                <button
                  key={c.id}
                  onClick={() => switchCategory(c.id)}
                  className="px-4 py-1.5 rounded-full text-sm transition-all"
                  style={{
                    background: active ? "var(--surface-2)" : "transparent",
                    color: active ? "var(--primary)" : "var(--text-muted)",
                    border: `1px solid ${active ? "var(--primary)" : "var(--divider)"}`,
                  }}
                  data-testid={`news-category-${c.id}`}
                >
                  {c.label}
                </button>
              );
            })}
            {originFilter && (
              <button
                onClick={clearCountryFocus}
                className="ml-1 px-3 py-1.5 rounded-full text-sm inline-flex items-center gap-1.5 transition-all"
                style={{
                  background: "color-mix(in oklab, var(--primary) 12%, transparent)",
                  color: "var(--primary)",
                  border: "1px solid var(--primary)",
                }}
                data-testid="news-origin-filter-chip"
                aria-label={`Clear ${originFilter.label} filter`}
              >
                <MapPin size={12} strokeWidth={1.75} />
                {flagEmoji(originFilter.code)} {originFilter.label}
                <XIcon size={12} strokeWidth={2} />
              </button>
            )}
          </div>

          {articleLoading && articles.length === 0 ? (
            <ArticleSkeleton />
          ) : articles.length === 0 ? (
            <EmptyNews />
          ) : (
            <>
              <div
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5"
                data-testid="news-articles-grid"
              >
                {articles.map((a, idx) => {
                  const h = hashByLink[a.link];
                  return (
                    <ArticleCard
                      key={`${a.link}-${idx}`}
                      article={a}
                      isSaved={h ? savedHashes.has(h) : false}
                      onToggleSave={() => toggleSave(a)}
                      onCountryTap={setCountryFocus}
                    />
                  );
                })}
              </div>
              {hasMore && (
                <div className="text-center mt-8">
                  <button
                    className="btn-ghost inline-flex items-center gap-2"
                    onClick={() => setArticleLimit((n) => n + LOAD_MORE_STEP)}
                    disabled={articleLoading}
                    data-testid="news-load-more-btn"
                  >
                    {articleLoading ? <RefreshCw size={14} className="animate-spin" /> : null}
                    Load more
                  </button>
                </div>
              )}
            </>
          )}
        </section>

        <div
          className="h-px w-full my-4"
          style={{ background: "linear-gradient(to right, transparent, var(--divider), transparent)" }}
        />

        {/* People */}
        <section>
          <header className="mb-5">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted">
              <Users size={14} strokeWidth={1.5} style={{ color: "var(--accent)" }} />
              Voices
            </div>
            <h2 className="font-display text-3xl sm:text-4xl mt-1" style={{ color: "var(--text)" }}>
              People shaping this field
            </h2>
            <p className="font-hand text-lg mt-2" style={{ color: "var(--accent)" }}>
              scientists worth following, not influencers
            </p>
            {regionLens === "asian" && (
              <p
                className="text-sm mt-3 px-3 py-2 rounded-xl"
                style={{
                  background: "color-mix(in oklab, var(--accent) 10%, transparent)",
                  color: "var(--text)",
                }}
                data-testid="news-asian-advisory"
              >
                Many Chinese and Asian researchers post more actively on WeChat and Weibo than X — consider following them on X where available, or bookmark their institutional pages.
              </p>
            )}
          </header>

          <div className="flex flex-wrap items-center gap-2 mb-6" role="tablist" data-testid="news-people-chips">
            {PEOPLE_TOPICS.map((t) => {
              const active = peopleTopic === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => setPeopleTopic(t.id)}
                  className="px-4 py-1.5 rounded-full text-sm transition-all"
                  style={{
                    background: active ? "var(--surface-2)" : "transparent",
                    color: active ? "var(--primary)" : "var(--text-muted)",
                    border: `1px solid ${active ? "var(--primary)" : "var(--divider)"}`,
                  }}
                  data-testid={`news-people-topic-${t.id}`}
                >
                  {t.label}
                </button>
              );
            })}
          </div>

          {peopleLoading && people.length === 0 ? (
            <PeopleSkeleton />
          ) : people.length === 0 ? (
            <div className="text-muted text-sm" data-testid="news-people-empty">No one filed under that yet.</div>
          ) : (
            <div
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
              data-testid="news-people-grid"
            >
              {people.map((p) => <PersonCard key={p.name} person={p} />)}
            </div>
          )}
        </section>
      </div>
    </AppShell>
  );
}

function ArticleCard({ article, isSaved, onToggleSave, onCountryTap }) {
  const { title, summary, link, source_name, published_at, image_url,
          origin_label, origin_country_code, native_language } = article;

  const handleBookmark = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onToggleSave && onToggleSave();
  };
  const handleCountry = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (origin_country_code) onCountryTap && onCountryTap(origin_country_code, origin_label);
  };

  return (
    <a
      href={link}
      target="_blank"
      rel="noopener noreferrer"
      className="card-soft p-0 overflow-hidden group flex flex-col hover:-translate-y-0.5 transition-transform duration-200 relative"
      data-testid="news-article-card"
    >
      <button
        type="button"
        onClick={handleBookmark}
        className="absolute top-3 right-3 z-10 p-2 rounded-full backdrop-blur-sm transition-transform active:scale-90"
        style={{
          background: "color-mix(in oklab, var(--surface) 82%, transparent)",
          color: isSaved ? "var(--primary)" : "var(--text-muted)",
          boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
        }}
        aria-label={isSaved ? "Remove from Read Weekend" : "Save to Read Weekend"}
        aria-pressed={isSaved ? true : false}
        data-testid="news-article-bookmark"
      >
        {isSaved
          ? <BookmarkCheck size={16} strokeWidth={1.75} data-testid="news-article-bookmark-saved" />
          : <Bookmark size={16} strokeWidth={1.75} />}
      </button>

      {image_url && (
        <div className="w-full aspect-[16/9] overflow-hidden" style={{ background: "var(--surface-2)" }}>
          <img
            src={image_url}
            alt=""
            loading="lazy"
            referrerPolicy="no-referrer"
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        </div>
      )}
      <div className="p-5 flex flex-col flex-1">
        <div className="flex items-center gap-2 text-xs flex-wrap">
          <span
            className="px-2 py-0.5 rounded-full"
            style={{
              background: "color-mix(in oklab, var(--primary) 10%, transparent)",
              color: "var(--primary)",
            }}
          >
            {source_name}
          </span>
          <span className="text-muted">· {formatRelative(published_at)}</span>
        </div>

        {origin_country_code && origin_label && (
          <button
            type="button"
            onClick={handleCountry}
            className="mt-2 self-start inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full transition-colors hover:opacity-80"
            style={{
              background: "var(--surface-2)",
              color: "var(--text-muted)",
            }}
            aria-label={`See more from ${origin_label}`}
            data-testid="news-article-origin-pill"
            data-country={origin_country_code}
          >
            <span aria-hidden="true">{flagEmoji(origin_country_code)}</span>
            <span>{origin_label}</span>
            {native_language && <span>· {native_language}</span>}
          </button>
        )}

        <h3 className="font-display text-lg leading-snug mt-3" style={{ color: "var(--text)" }}>
          {title}
        </h3>
        {summary && (
          <p className="text-sm mt-2 text-muted" style={{ display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
            {summary}
          </p>
        )}
        <div className="mt-4 inline-flex items-center gap-1 text-sm font-medium" style={{ color: "var(--primary)" }}>
          Read article
          <ExternalLink size={12} strokeWidth={1.75} />
        </div>
      </div>
    </a>
  );
}

function PersonCard({ person }) {
  const {
    name, name_native, x_handle, x_url, bio_short, topic_tags, affiliation,
    institutional_url, weibo_url, wechat_id, google_scholar_url, personal_site_url,
  } = person;
  const hasNative = Boolean(name_native);

  const secondaryLinks = [
    x_handle && x_url && { key: "x",      url: x_url,               label: "Follow on X",                Icon: Twitter },
    weibo_url             && { key: "weibo",  url: weibo_url,           label: "Follow on Weibo",             Icon: MessageCircle },
    wechat_id             && { key: "wechat", url: `https://weixin.qq.com/`, label: `WeChat: ${wechat_id}`, Icon: MessageCircle, plain: true, badge: wechat_id },
    institutional_url     && { key: "inst",   url: institutional_url,   label: "Institutional profile",      Icon: GraduationCap },
    google_scholar_url    && { key: "scholar",url: google_scholar_url,  label: "Read on Google Scholar",     Icon: GraduationCap },
    personal_site_url     && { key: "site",   url: personal_site_url,   label: "Personal site",              Icon: Link2 },
  ].filter(Boolean);

  return (
    <div className="card-soft p-5 flex flex-col" data-testid="news-person-card">
      <div className="flex items-start gap-3">
        <div
          className="flex-shrink-0 w-14 h-14 rounded-full flex items-center justify-center font-display text-xl select-none"
          style={{
            background: "color-mix(in oklab, var(--accent) 20%, var(--surface-2))",
            color: "var(--accent)",
            fontFamily: hasNative
              ? "'Noto Sans SC', 'Nunito', system-ui, sans-serif"
              : undefined,
          }}
          aria-hidden="true"
          data-testid="news-person-avatar"
        >
          {initials(name, name_native)}
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display text-base leading-tight" style={{ color: "var(--text)" }}>{name}</div>
          {hasNative && (
            <div
              className="text-sm leading-tight mt-0.5"
              style={{
                color: "var(--text-muted)",
                fontFamily: "'Noto Sans SC', 'Nunito', system-ui, sans-serif",
              }}
              data-testid="news-person-native-name"
            >
              {name_native}
            </div>
          )}
          {x_handle ? (
            <div className="text-xs text-muted mt-0.5">@{x_handle}</div>
          ) : affiliation ? (
            <div className="text-xs text-muted mt-0.5">{affiliation}</div>
          ) : null}
        </div>
      </div>
      <p className="text-sm mt-3" style={{ color: "var(--text-muted)" }}>{bio_short}</p>
      <div className="flex flex-wrap gap-1.5 mt-3">
        {(topic_tags || []).map((t) => (
          <span
            key={t}
            className="text-[11px] px-2 py-0.5 rounded-full"
            style={{
              background: "var(--surface-2)",
              color: "var(--text-muted)",
            }}
          >
            {t.replace(/_/g, " ")}
          </span>
        ))}
      </div>
      <a
        href={x_url}
        target="_blank"
        rel="noopener noreferrer"
        className="btn-primary mt-4 inline-flex items-center justify-center gap-1.5 text-sm"
        data-testid="news-person-follow-btn"
      >
        {x_handle ? "Follow on X" : "Visit page"}
        <ExternalLink size={12} strokeWidth={1.75} />
      </a>
      {secondaryLinks.length > 0 && (
        <div className="flex items-center gap-1 mt-3 flex-wrap" data-testid="news-person-links-row">
          {secondaryLinks.map(({ key, url, label, Icon }) => (
            <a
              key={key}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              title={label}
              aria-label={label}
              className="p-1.5 rounded-full transition-colors hover:bg-[var(--surface-2)]"
              style={{ color: "var(--text-muted)" }}
              data-testid={`news-person-link-${key}`}
            >
              <Icon size={16} strokeWidth={1.75} />
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

function ArticleSkeleton() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="news-articles-skeleton">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="card-soft p-0 overflow-hidden">
          <div className="w-full aspect-[16/9]" style={{ background: "var(--surface-2)" }} />
          <div className="p-5 space-y-3">
            <div className="h-3 rounded" style={{ background: "var(--surface-2)", width: "40%" }} />
            <div className="h-4 rounded" style={{ background: "var(--surface-2)", width: "90%" }} />
            <div className="h-3 rounded" style={{ background: "var(--surface-2)", width: "70%" }} />
          </div>
        </div>
      ))}
    </div>
  );
}

function PeopleSkeleton() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="news-people-skeleton">
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="card-soft p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-14 h-14 rounded-full" style={{ background: "var(--surface-2)" }} />
            <div className="flex-1 space-y-2">
              <div className="h-3 rounded" style={{ background: "var(--surface-2)", width: "70%" }} />
              <div className="h-2 rounded" style={{ background: "var(--surface-2)", width: "40%" }} />
            </div>
          </div>
          <div className="h-3 rounded" style={{ background: "var(--surface-2)", width: "90%" }} />
        </div>
      ))}
    </div>
  );
}

function EmptyNews() {
  return (
    <div className="card-soft p-10 text-center" data-testid="news-empty">
      <div className="font-display text-2xl mb-2" style={{ color: "var(--text)" }}>The field is quiet today.</div>
      <p className="text-sm text-muted">Try again in a bit — the labs are surely writing something.</p>
    </div>
  );
}
